import React from "react";
import { MAuswahl, MAuswahlwert } from "../../metamodell";
import { InfoBox } from "./InfoBox";
import { getAuswahlData } from "./MHelper";

/**
 * Standard-Auswahl. Kann mit Auswahlwerten gefüttert werden oder holt sich diese von der Konfiguration
 * @param optionKey Auswahlname (Füllung, ...)
 * @param optionTitleKey optional, custom Titel
 * @param options optional, Auswahlwerte zum anzeigen
 * @param selectFunction optional, setter Funktion für diese Auswahl
 * @constructor
 */
export function OptionSelect({
  optionKey,
  optionTitleKey,
  options,
  selectFunction = (what: string, auswahlwert: MAuswahlwert) =>
    APP.arrangement.konfiguration.setAuswahlwert(what, auswahlwert),
  noTitleBlock = false,
}: {
  optionKey: string;
  optionTitleKey?: string;
  options?: MAuswahlwert[];
  selectFunction?: (what: string, auswahlwert: MAuswahlwert) => void;
  noTitleBlock?: boolean;
}) {
  const konf = APP.arrangement.konfiguration;
  const auswahlwerte = options || konf.getPassendeAuswahlwerte(optionKey);
  const titleKey = optionTitleKey || optionKey;
  const auswahlData = getAuswahlData(titleKey);

  const selectedAuswahlwert = konf.getAuswahlwert(optionKey);

  if (Commons.isEmpty(auswahlwerte) || auswahlwerte.length < 2 || Commons.isEmpty(auswahlData))
    return <></>;

  return (
    <section
      className={"optionSelect " + titleKey}
      role="radiogroup"
      aria-labelledby={`title-${titleKey}`}
    >
      {!noTitleBlock && <TitleBlock auswahlData={auswahlData} titleId={`title-${titleKey}`} />}
      <div className="options">
        {auswahlwerte
          ?.sort((a, b) => (a.Sortierung ?? 0) - (b.Sortierung ?? 0))
          .map((auswahlwert: MAuswahlwert) => (
            <Option
              key={auswahlwert.Key}
              optionKey={optionKey}
              isSelected={auswahlwert == selectedAuswahlwert}
              auswahlwert={auswahlwert}
              selectFunction={selectFunction}
            />
          ))}
      </div>
    </section>
  );
}

/**
 * Titelleiste für eine Auswahl
 * @param auswahlData Auswahl, die dargestellt wird (Alternativ: text)
 * @param text Wird angezeigt falls auswahlData nicht angegeben
 * @param small Kleine Auswahl (inline), z.B. bei Massen
 * @constructor
 */
export function TitleBlock({
  auswahlData,
  text,
  small,
  textColor,
  titleId,
}: {
  auswahlData?: MAuswahl;
  text?: string;
  small?: boolean;
  textColor?: string;
  titleId?: string;
}) {
  const titleText = auswahlData?.TextMass || auswahlData?.Text || text || "";
  const showInfo = !Commons.isEmpty(auswahlData?.Hilfstext);
  const color = "#" + textColor;

  const TitleTag = small ? "div" : "h3";

  return (
    <div className={"titleBlock"}>
      <TitleTag className={`title ${small ? "smallTitle" : ""}`} id={titleId}>
        <span dangerouslySetInnerHTML={{ __html: titleText }} style={{ color: color }}></span>
        {showInfo && <InfoBox auswahlData={auswahlData} small={small}></InfoBox>}
      </TitleTag>
    </div>
  );
}

/**
 * Einzelnes anklickbares Feld, welches einen einzelnen Auswahlwert repräsentiert
 * @param optionKey Name der Auswahl z.B. Füllung
 * @param auswahlwert darzustellender Auswahlwert
 * @param selectFunction setter-Funktion
 * @param isSelected optional, ist dieser Auswahlwert gerade ausgewählt?
 * @param isRecommended optional, ist dieser Auswahlwert empfohlen?
 * @constructor
 */
export function Option({
  optionKey,
  auswahlwert,
  selectFunction,
  isSelected = false,
}: {
  optionKey: string;
  auswahlwert: MAuswahlwert;
  selectFunction: Function;
  isSelected?: boolean;
}) {
  function getBezeichnung(objekt: any) {
    return (
      objekt.Auswahlwert?.Bezeichnung ||
      objekt.Auswahlwert?.Name ||
      objekt.Bezeichnung ||
      objekt.Name ||
      objekt.Key ||
      ""
    );
  }

  if (Commons.isEmpty(auswahlwert) || auswahlwert.GesperrtMass) return null;
  const name = getBezeichnung(auswahlwert);
  const bild = auswahlwert.Auswahlbild ? (
    <img className="bgImage" alt={name} src={auswahlwert.Auswahlbild} />
  ) : null;
  return (
    <button
      key={auswahlwert.Key}
      role="radio"
      aria-label={auswahlwert.AriaText || name}
      aria-checked={isSelected}
      data-testid={auswahlwert.Key}
      data-auswahlwert-key={auswahlwert.Key}
      className={`option ${isSelected ? "selected" : ""}`}
      onClick={() => selectFunction(optionKey, auswahlwert)}
    >
      {bild}
      <div className={`description ${bild ? "bgGrey" : ""} ${name.length > 50 ? "smallFont" : ""}`}>
        <i
          className={`icon ${isSelected ? "icon-radio-checked" : "icon-radio-unchecked"}`}
          aria-hidden="true"
        ></i>
        {auswahlwert.Prefix && (
          <span
            className="prefix"
            dangerouslySetInnerHTML={{ __html: auswahlwert.Prefix }}
            aria-hidden="true"
          ></span>
        )}
        <span dangerouslySetInnerHTML={{ __html: name }} aria-hidden="true"></span>
      </div>
      <i className="shiny" aria-hidden="true"></i>
    </button>
  );
}
